import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Plus, Truck } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import {
  CONSULTA_TIPOS,
  type Detalle,
  FormularioProveedor,
  type Proveedor,
  type TipoProveedor,
} from "@/componentes/catalogos/FormularioProveedor"
import { PerfilProveedor } from "@/componentes/catalogos/PerfilProveedor"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Catálogo de proveedores.
 *
 * La disposición viene del ERP hermano —resumen arriba, buscador con filtros de
 * categoría, tabla ancha y ficha lateral— y los materiales son los de esta casa: el
 * cristal, sus insignias y sus tokens. Copiar además su paleta habría metido un
 * segundo lenguaje visual dentro del mismo armazón.
 *
 * ⚠ **Editar pide el DETALLE, no reutiliza la fila del listado.** El listado no trae
 * los datos bancarios a propósito —`ProveedorOut` no los declara— así que abrir el
 * formulario con la fila dejaría el banco y la cuenta en blanco, y guardar los
 * borraría sin que nadie lo hubiera pedido.
 *
 * ⚠ **La jerarquía se muestra, no se adivina.** Un proveedor con padre se pinta
 * indicando de quién depende: son las agencias de una naviera y las filiales de un
 * grupo, y verlas sueltas hace que alguien registre la misma factura dos veces.
 *
 * ⚠ **El filtro por categoría se calcula en el navegador y la BÚSQUEDA no.** La
 * categoría vive en el tipo, que ya está cargado para pintar la columna; buscar sí
 * va al servidor, porque la paginación es suya (`.claude/rules/frontend.md`).
 */

type Categoria = TipoProveedor["categorias"][number]

const CATEGORIAS: readonly { valor: Categoria | "TODAS"; etiqueta: string }[] = [
  { valor: "TODAS", etiqueta: "Todos" },
  { valor: "OPERATIVO", etiqueta: "Operativos" },
  { valor: "ADMINISTRATIVO", etiqueta: "Administrativos" },
  { valor: "SERVICIO_PROPIO", etiqueta: "Servicio propio" },
]

/**
 * (28/10) Cada departamento abre SUS proveedores: los operativos —almacenadora, naviera,
 * transporte, a los que se cargan gastos del expediente— en Operaciones Aduaneras, y los
 * administrativos —gasto de la agencia— en Administración. Lo pidió el usuario: «hay
 * proveedores operativos y administrativos, ambos con lógicas y necesidades distintas».
 * Es el mismo catálogo con la categoría FIJA, no una copia: un proveedor de las dos sale en
 * las dos. `/proveedores` a secas sigue enseñando todos.
 */
const AMBITOS = {
  operativos: { categoria: "OPERATIVO", titulo: "Proveedores operativos" },
  administrativos: { categoria: "ADMINISTRATIVO", titulo: "Proveedores administrativos" },
} as const satisfies Record<string, { categoria: Categoria; titulo: string }>

export function Proveedores({ deAmbito }: { deAmbito?: keyof typeof AMBITOS } = {}) {
  const usuario = usarSesion((e) => e.usuario)
  const ambito = deAmbito === undefined ? undefined : AMBITOS[deAmbito]
  const [buscar, setBuscar] = useState("")
  const [soloActivos, setSoloActivos] = useState(true)
  const [elegida, setCategoria] = useState<Categoria | "TODAS">("TODAS")
  const categoria = ambito?.categoria ?? elegida
  const [creando, setCreando] = useState(false)
  const [editandoId, setEditandoId] = useState<number | null>(null)
  const [abiertoId, setAbiertoId] = useState<number | null>(null)
  const caja = useRef<HTMLDivElement | null>(null)

  const parametros = new URLSearchParams({ limit: "100" })
  if (buscar.trim() !== "") parametros.set("buscar", buscar.trim())
  if (soloActivos) parametros.set("activo", "true")

  const consulta = useQuery({
    queryKey: ["proveedores", { buscar: buscar.trim(), soloActivos }],
    queryFn: () => api.get<Proveedor[]>(`/api/v1/proveedores?${parametros}`),
  })

  const tipos = useQuery(CONSULTA_TIPOS)

  // Sólo cuando hay uno abierto: el listado no trae los datos bancarios.
  const detalle = useQuery({
    queryKey: ["proveedor", editandoId],
    queryFn: () => api.get<Detalle>(`/api/v1/proveedores/${editandoId}`),
    enabled: editandoId !== null,
  })

  const puedeAdministrar = tienePermiso(usuario, "proveedor:administrar")
  const borde = { borderColor: "var(--border)" }
  const todas = consulta.data ?? []
  const nombreDe = new Map(todas.map((p) => [p.id, p.nombre]))
  const tipoDe = new Map((tipos.data ?? []).map((t) => [t.id, t]))

  /**
   * Las categorías de un proveedor: la UNIÓN de las de todos sus tipos.
   *
   * ⚠ **Un proveedor puede ser dos cosas**, y entonces sale en las dos pestañas: un
   * ente gubernamental cobra la tasa del puerto —operativo— y la patente municipal
   * —administrativo—. Es la misma cuenta que hace el servidor (`categorias_de`).
   */
  const categoriasDe = (p: Proveedor): Set<string> => {
    const ids = [...(p.tipos_adicionales ?? [])]
    if (p.tipo_proveedor_id !== null) ids.push(p.tipo_proveedor_id)
    return new Set(ids.flatMap((id) => tipoDe.get(id)?.categorias ?? []))
  }

  const filas = categoria === "TODAS" ? todas : todas.filter((p) => categoriasDe(p).has(categoria))

  const cuenta = (valor: Categoria) => todas.filter((p) => categoriasDe(p).has(valor)).length

  return (
    <div>
      {/* Con título propio: la sección del menú es el departamento, no una pantalla
          que contenga a ésta. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{ambito?.titulo ?? "Proveedores"}</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            A quién se le compra y a quién se le paga.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          {/* ⚠ El título dice QUÉ catálogo es: el mismo componente sirve a los
            operativos y a los administrativos, y un archivo «Proveedores» a secas no
            dice cuál de los dos se exportó. */}
          <Exportar
            titulo={ambito?.titulo ?? "Proveedores"}
            subtitulo={[
              ambito === undefined && categoria !== "TODAS"
                ? CATEGORIAS.find((c) => c.valor === categoria)?.etiqueta
                : null,
              soloActivos ? "Sólo activos" : "Activos e inactivos",
              buscar.trim() && `Búsqueda: «${buscar.trim()}»`,
            ]
              .filter(Boolean)
              .join(" · ")}
            contenido={caja}
          />
          {puedeAdministrar && (
            <button
              type="button"
              onClick={() => {
                setEditandoId(null)
                setCreando((abierto) => !abierto)
              }}
              aria-expanded={creando}
              className="flex h-9 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-medium"
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
            >
              <Plus size={16} aria-hidden="true" />
              Nuevo proveedor
            </button>
          )}
        </div>
      </div>

      {/* El resumen va arriba porque es lo que se mira sin buscar nada: cuántos hay
          y de qué clase. Las cifras salen de lo cargado, no de una consulta aparte. */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Resumen etiqueta="En el catálogo" valor={todas.length} />
        <Resumen etiqueta="Operativos" valor={cuenta("OPERATIVO")} />
        <Resumen etiqueta="Administrativos" valor={cuenta("ADMINISTRATIVO")} />
        <Resumen
          etiqueta="A crédito"
          valor={todas.filter((p) => p.condicion_pago === "CREDITO").length}
        />
      </div>

      {creando && (
        <div className="mb-6">
          <FormularioProveedor
            proveedor={null}
            tipos={tipos.data ?? []}
            candidatosPadre={todas}
            onCerrar={() => setCreando(false)}
          />
        </div>
      )}

      {editandoId !== null && (
        <div className="mb-6">
          {detalle.isPending && (
            <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Cargando el proveedor…
            </p>
          )}
          {detalle.data && (
            <FormularioProveedor
              proveedor={detalle.data}
              tipos={tipos.data ?? []}
              candidatosPadre={todas}
              onCerrar={() => setEditandoId(null)}
            />
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <label className="text-sm" htmlFor="buscar-proveedor">
          Buscar
          <input
            id="buscar-proveedor"
            className="ml-2 h-9 rounded-[var(--radius-control)] border px-2 text-sm"
            style={borde}
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Nombre, código o RIF"
          />
        </label>
        {ambito === undefined && (
          <fieldset className="flex flex-wrap gap-1">
            <legend className="sr-only">Filtrar por categoría</legend>
            {CATEGORIAS.map((c) => (
              <button
                key={c.valor}
                type="button"
                onClick={() => setCategoria(c.valor)}
                aria-pressed={categoria === c.valor}
                // Lo elegido sube la luz con `.activo-cristal`, el mismo gesto que el menú
                // y las pestañas: un color de texto suelto sería una tercera señal.
                className={`rounded-[var(--radius-control)] px-2.5 py-1 text-sm foco-cristal ${
                  categoria === c.valor ? "activo-cristal" : "toque-cristal"
                }`}
              >
                {c.etiqueta}
              </button>
            ))}
          </fieldset>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={soloActivos}
            onChange={(e) => setSoloActivos(e.target.checked)}
          />
          Sólo activos
        </label>
      </div>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando proveedores…
        </p>
      )}

      {consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            {consulta.error instanceof ErrorApi
              ? consulta.error.message
              : "No se pudieron cargar los proveedores"}
          </p>
        </div>
      )}

      {consulta.data && filas.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <Truck size={24} aria-hidden="true" />
          <p className="text-sm">
            {buscar.trim() !== "" || categoria !== "TODAS"
              ? "Ningún proveedor coincide"
              : "Todavía no hay proveedores"}
          </p>
        </div>
      )}

      <div ref={caja}>
        {consulta.data && filas.length > 0 && (
          <Tabla etiqueta="Proveedores registrados">
            <Cabecera>
              <Th>Código</Th>
              <Th>Nombre</Th>
              <Th>RIF</Th>
              <Th>Tipo</Th>
              <Th>Contacto</Th>
              <Th>Pago</Th>
              <Th>Estado</Th>
              {puedeAdministrar && <Th />}
            </Cabecera>
            <Cuerpo>
              {filas.map((p) => (
                <Fila
                  key={p.id}
                  data-testid={`proveedor-${p.id}`}
                  className="cursor-pointer"
                  onClick={() => setAbiertoId(p.id)}
                >
                  <Td numero className="font-medium">
                    {p.codigo}
                  </Td>
                  <Td>
                    {p.nombre}
                    {p.proveedor_padre_id !== null && (
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        depende de {nombreDe.get(p.proveedor_padre_id) ?? "otro proveedor"}
                      </span>
                    )}
                  </Td>
                  <Td numero>{p.rif ?? "—"}</Td>
                  {/* El principal NOMBRA la fila; los demás se dicen debajo, que es
                    para lo que existe el principal (ver `FormularioProveedor`). */}
                  <Td style={{ color: "var(--fg-muted)" }}>
                    {p.tipo_proveedor_id === null
                      ? "Sin tipo"
                      : (tipoDe.get(p.tipo_proveedor_id)?.nombre ?? "—")}
                    {(p.tipos_adicionales ?? []).length > 0 && (
                      <span className="block text-xs">
                        y{" "}
                        {(p.tipos_adicionales ?? [])
                          .map((id) => tipoDe.get(id)?.nombre ?? "—")
                          .join(", ")}
                      </span>
                    )}
                  </Td>
                  <Td style={{ color: "var(--fg-muted)" }}>
                    {p.email ?? p.telefono ?? "—"}
                    {p.email && p.telefono && (
                      <span className="block text-xs tabular-nums">{p.telefono}</span>
                    )}
                  </Td>
                  <Td>
                    {p.condicion_pago === "CREDITO" ? (
                      <Insignia tono="info">{p.dias_credito} días</Insignia>
                    ) : (
                      <span style={{ color: "var(--fg-muted)" }}>Contado</span>
                    )}
                  </Td>
                  <Td>
                    <Insignia tono={p.activo ? "exito" : "neutro"}>
                      {p.activo ? "Activo" : "Inactivo"}
                    </Insignia>
                  </Td>
                  {puedeAdministrar && (
                    <Td derecha>
                      <button
                        type="button"
                        data-testid={`editar-proveedor-${p.id}`}
                        onClick={(evento) => {
                          // La fila abre la ficha; el botón edita. Sin esto, editar
                          // abriría además el cajón por detrás del formulario.
                          evento.stopPropagation()
                          setCreando(false)
                          setAbiertoId(null)
                          setEditandoId(p.id)
                        }}
                        className="rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                        style={borde}
                      >
                        Editar
                      </button>
                    </Td>
                  )}
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        )}
      </div>

      {abiertoId !== null && (
        <PerfilProveedor
          proveedorId={abiertoId}
          onCerrar={() => setAbiertoId(null)}
          onEditar={() => {
            setEditandoId(abiertoId)
            setAbiertoId(null)
          }}
        />
      )}
    </div>
  )
}

function Resumen({ etiqueta, valor }: { etiqueta: string; valor: number }) {
  return (
    <div className="tarjeta p-4">
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}
      </p>
      <p className="text-2xl font-semibold tabular-nums">{valor}</p>
    </div>
  )
}
